import React from 'react'
import { Mail, Phone, Shield, User as UserIcon } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar'

interface ProfileCardProps {
  user: {
    id?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    avatar?: string
    isAdmin?: boolean
  }
  currentUserId?: string
  className?: string
}

export const ProfileCard: React.FC<ProfileCardProps> = ({ user, currentUserId, className = '' }) => {
  const getInitials = () => {
    const firstInitial = user.firstName?.[0] || ''
    const lastInitial = user.lastName?.[0] || ''
    return (firstInitial + lastInitial).toUpperCase() || 'U'
  }

  const isCurrentUser = currentUserId === user.id

  return (
    <div className={`flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/80 dark:to-slate-800/60 border border-slate-200 dark:border-slate-700 ${className}`}>
      {/* Avatar */}
      <Avatar className="h-16 w-16 ring-4 ring-white dark:ring-slate-700 shadow-lg">
        <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
        <AvatarFallback className="bg-gradient-to-br from-[#69adba] to-[#5a9aa6] text-white text-xl font-bold">
          {getInitials()}
        </AvatarFallback>
      </Avatar>

      {/* User Info */}
      <div className="flex-1 min-w-0">
        {/* Name and Badges */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">
            {user.firstName} {user.lastName}
          </h3>
          
          {/* "You" Badge */}
          {isCurrentUser && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
              <UserIcon className="h-3 w-3" />
              You
            </span>
          )}
          
          {/* "Account Owner" / "Admin" Badge */}
          {user.isAdmin && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700">
              <Shield className="h-3 w-3" />
              Account Owner
            </span>
          )}
        </div>

        {/* Contact Info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
          {user.email && (
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Mail className="h-4 w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
              <span className="truncate">{user.email}</span>
            </div>
          )}
          {user.phone && (
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Phone className="h-4 w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
              <span>{user.phone}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}




